import React, { useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import moment from "moment";
import classNames from "classnames";

import { Avatar, Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import { TagV2 } from "@toddle-edu/ds-web/dist/components/Tag";
import {
  ProgressReportOutlined,
  CommentRemarkOutlined,
} from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { getIsStudentReflectionEnabledInReports } from "ProgressReportCommon/utils";
import ReflectionFeedbackModal from "AppComponents/ProgressReportCommon/components/ReflectionFeedbackModal";

import classes from "./OngoingReflectionCard.scss";

const OngoingReflectionCard = props => {
  const {
    studentProgressReportId,
    progressReportName,
    reflectionDeadline,
    isReflectionEditable,
    academicTerms,
    reflectionFeedback,
    onActionClick,
  } = props;

  const { t } = useI18n([]);
  const [isRemarkOpen, setIsRemarkOpen] = useState(false);

  const termsLabel = _.map(academicTerms, "label").join(", ");
  const formattedDeadline = reflectionDeadline
    ? moment(reflectionDeadline).format(
        DATE_TIME_CONSTANTS.Do_MMM_YYYY_NO_COMMA
      )
    : "";

  const showPendingTag = isReflectionEditable;
  const isStudentReflectionEnabledInReports = getIsStudentReflectionEnabledInReports();
  const hasRemark =
    isStudentReflectionEnabledInReports &&
    !_.isEmpty(_.get(reflectionFeedback, "label", ""));

  return (
    <article
      className={classes.cardContainer}
      data-test-id="ongoingReflectionCard-container"
    >
      <div className={classes.leftContent}>
        <Avatar
          id={studentProgressReportId}
          shape="square"
          iconType="icon"
          size="medium"
          icon={<ProgressReportOutlined />}
        />
        <div className={classes.textContent}>
          <div className={classes.titleRow}>
            <span className={classNames("text-label", classes.reportName)}>
              {progressReportName}
            </span>
            {showPendingTag && (
              <TagV2 size="small" color="green">
                {t("common:submission_pending")}
              </TagV2>
            )}
          </div>
          <div className={classes.metaRow}>
            {termsLabel && (
              <span className={classNames("text-body-s", classes.metaText)}>
                {termsLabel}
              </span>
            )}
            {termsLabel && formattedDeadline && (
              <span className={classNames("text-body-s", classes.dotSeparator)}>
                {"•"}
              </span>
            )}
            {formattedDeadline && (
              <span className={classNames("text-body-s", classes.metaText)}>
                {t("common:deadline_with_value", {
                  value: formattedDeadline,
                })}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={classes.actionsRow}>
        {hasRemark ? (
          <Tooltip dsVersion="2.0" tooltip={t("progressReport:view_remark")}>
            <IconButton
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              size="medium"
              icon={<CommentRemarkOutlined />}
              onClick={() => setIsRemarkOpen(true)}
              testId="ongoingReflectionCard-viewRemarkButton"
            />
          </Tooltip>
        ) : null}
        <Button
          variant={isReflectionEditable ? "progressive" : "neutral"}
          type={isReflectionEditable ? "fill" : "outlined"}
          size="medium"
          onClick={onActionClick}
          testId="ongoingReflectionCard-actionButton"
        >
          {isReflectionEditable
            ? t("progressReport:enter_reflection")
            : t("progressReport:view_reflection")}
        </Button>
      </div>
      {isRemarkOpen && reflectionFeedback ? (
        <ReflectionFeedbackModal
          title={t("assessmentEvaluation:teacher_remark")}
          feedback={reflectionFeedback}
          onClose={() => setIsRemarkOpen(false)}
        />
      ) : null}
    </article>
  );
};

OngoingReflectionCard.propTypes = {
  studentProgressReportId: PropTypes.string.isRequired,
  progressReportName: PropTypes.string.isRequired,
  reflectionDeadline: PropTypes.string,
  isReflectionEditable: PropTypes.bool.isRequired,
  academicTerms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  reflectionFeedback: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    isResolved: PropTypes.bool,
    createdBy: PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      profileImage: PropTypes.string,
    }),
  }),
  onActionClick: PropTypes.func.isRequired,
};

OngoingReflectionCard.defaultProps = {
  reflectionDeadline: "",
  academicTerms: [],
  reflectionFeedback: null,
};

export default OngoingReflectionCard;
